import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { initials, fmtTime } from "@/lib/helpers";
import { PaperPlaneTilt, Plus, ChatCircle, ArrowLeft, MagnifyingGlass } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

import { devLog } from "@/lib/devLog";
function wsUrl() {
  const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const url = new URL(backend);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/api/ws/messages";
  // Auth is carried by the same-origin `access_token` cookie the browser
  // attaches on the WebSocket handshake — no JS-readable token needed.
  return url.toString();
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);
  const wsRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadThread = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/messages/threads/${id}`);
      setMessages(data.messages || []);
      // update local thread with unread=0
      setThreads(ts => ts.map(t => t.id === id ? { ...t, unread: 0 } : t));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadThreads();
      setLoading(false);
    })();
  }, [loadThreads]);

  useEffect(() => {
    if (activeId) loadThread(activeId);
    else setMessages([]);
  }, [activeId, loadThread]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, activeId]);

  // WebSocket
  useEffect(() => {
    let closed = false;
    const connect = () => {
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "message.new") {
            if (payload.thread_id === activeId) {
              setMessages(ms => [...ms, payload.message]);
              // acknowledge read
              api.get(`/messages/threads/${payload.thread_id}`).catch(() => {});
            } else {
              setThreads(ts => ts.map(t => t.id === payload.thread_id
                ? { ...t, unread: (t.unread || 0) + 1, last_message: payload.message, last_message_at: payload.message.at }
                : t));
            }
          } else if (payload.type === "thread.created") {
            loadThreads();
          }
        } catch (err) { devLog("ws message parse failed", err); }
      };
      ws.onclose = () => {
        if (!closed) setTimeout(connect, 2000);
      };
      const ping = setInterval(() => {
        if (ws.readyState === 1) ws.send("ping");
      }, 25000);
      ws._ping = ping;
    };
    connect();
    return () => {
      closed = true;
      if (wsRef.current) {
        clearInterval(wsRef.current._ping);
        wsRef.current.close();
      }
    };
  }, [activeId, loadThreads]);

  const activeThread = threads.find(t => t.id === activeId);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !activeId) return;
    const body = text.trim();
    setText("");
    try {
      await api.post(`/messages/threads/${activeId}/messages`, { text: body });
      // ws will echo back and add to list
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="overline mb-1.5">Inbox</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Messages</h1>
          <p className="text-sm text-muted-foreground mt-1">Realtime chat with your team, therapists, and guardians.</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-thread-btn"><Plus size={16} weight="bold" />New chat</Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 md:min-h-[520px]">
        {/* Threads list */}
        <Card className="md:col-span-1 h-fit max-h-[70vh] overflow-y-auto">
          <div className="p-3 sticky top-0 bg-card border-b border-border">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Filter…" className="pl-8 h-9" data-testid="thread-filter" />
            </div>
          </div>
          {loading ? <div className="p-4 text-sm text-muted-foreground">Loading…</div> :
           threads.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <ChatCircle size={28} className="mx-auto mb-2 opacity-40" weight="duotone" />
              No conversations yet. Start one!
            </div>
          ) : (
            <div className="divide-y divide-border">
              {threads.map(t => {
                const others = t.other_participants || [];
                const label = others.length ? others.map(o => o.name).join(", ") : (t.subject || "Chat");
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveId(t.id)}
                    data-testid={`thread-${t.id}`}
                    className={cn(
                      "w-full px-3 py-3 flex items-start gap-3 text-left hover:bg-muted/40",
                      activeId === t.id && "bg-primary/5 border-l-2 border-primary"
                    )}
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials(label)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium truncate">{label}</div>
                        {t.unread > 0 && <Badge className="bg-primary text-primary-foreground text-[10px] h-5 min-w-5 px-1.5">{t.unread}</Badge>}
                      </div>
                      {t.subject && <div className="text-xs text-muted-foreground truncate">{t.subject}</div>}
                      {t.last_message && (
                        <div className="text-xs text-muted-foreground truncate mt-0.5">
                          {t.last_message.text}
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground whitespace-nowrap">{fmtTime(t.last_message_at)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Chat pane */}
        <Card className="md:col-span-2 flex flex-col max-h-[70vh]">
          {activeThread ? (
            <>
              <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setActiveId(null)}><ArrowLeft size={16} /></Button>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    {(activeThread.other_participants || []).map(o => o.name).join(", ") || activeThread.subject}
                  </div>
                  {activeThread.subject && <div className="text-xs text-muted-foreground truncate">{activeThread.subject}</div>}
                </div>
                <span className="relative flex h-2 w-2"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /></span>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
                {messages.map(m => {
                  const mine = m.sender_id === user?.id;
                  const sender = (activeThread.participants || []).find(p => p.id === m.sender_id);
                  return (
                    <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                      <div className={cn(
                        "max-w-[75%] rounded-lg px-3 py-2 shadow-sm",
                        mine ? "bg-primary text-primary-foreground" : "bg-card border border-border"
                      )}>
                        {!mine && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{sender?.name || "?"}</div>}
                        <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                        <div className={cn("text-[10px] mt-1", mine ? "text-primary-foreground/70" : "text-muted-foreground")}>{fmtTime(m.at)}</div>
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <div className="text-sm text-muted-foreground text-center py-8">No messages yet — say hello!</div>}
              </div>

              <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
                <Textarea
                  value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
                  className="min-h-[42px] max-h-32 resize-none"
                  data-testid="message-input"
                />
                <Button type="submit" size="icon" data-testid="message-send"><PaperPlaneTilt size={18} weight="fill" /></Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-8 text-center">
              <div>
                <ChatCircle size={40} className="mx-auto mb-3 opacity-40" weight="duotone" />
                Pick a conversation on the left or start a new one.
              </div>
            </div>
          )}
        </Card>
      </div>

      <NewThreadDialog open={showNew} onOpenChange={setShowNew} onCreated={(t) => { setShowNew(false); setActiveId(t.id); loadThreads(); }} />
    </div>
  );
}

function NewThreadDialog({ open, onOpenChange, onCreated }) {
  const [directory, setDirectory] = useState([]);
  const [selected, setSelected] = useState({});
  const [subject, setSubject] = useState("");
  const [msg, setMsg] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/messages/directory");
        setDirectory(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    setSelected({}); setSubject(""); setMsg(""); setSearch("");
  }, [open]);

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const filtered = directory.filter(u => u.name.toLowerCase().includes(search.toLowerCase()));
  const count = Object.values(selected).filter(Boolean).length;

  const submit = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) { toast.error("Pick at least one contact"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/messages/threads", {
        participant_ids: ids,
        subject: subject.trim() || undefined,
        initial_message: msg.trim() || undefined,
      });
      onCreated?.(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-tight">New chat</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <div className="text-xs font-medium">Contacts ({count} selected)</div>
            <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search contacts…" data-testid="thread-search" />
            <div className="border border-border rounded-md max-h-52 overflow-y-auto">
              {filtered.map(u => (
                <label key={u.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40 ${selected[u.id] ? "bg-primary/5" : ""}`}>
                  <input type="checkbox" checked={!!selected[u.id]} onChange={() => toggle(u.id)} data-testid={`contact-${u.id}`} />
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials(u.name)}</AvatarFallback></Avatar>
                  <div className="flex-1"><div className="text-sm">{u.name}</div><div className="text-xs text-muted-foreground">{u.roles?.join(", ")}</div></div>
                </label>
              ))}
              {filtered.length === 0 && <div className="p-4 text-xs text-muted-foreground text-center">No contacts.</div>}
            </div>
          </div>
          <div className="space-y-1.5"><label className="text-xs font-medium">Subject (optional)</label><Input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="e.g. Aarav's Wednesday session" /></div>
          <div className="space-y-1.5"><label className="text-xs font-medium">First message (optional)</label><Textarea rows={3} value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Say hi…" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || count === 0} data-testid="create-thread">{saving ? "Creating…" : "Start chat"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
